import { cloneAdminProspectTargeting } from './prospectTargetingFilters.js';

export const ADMIN_PROSPECT_SELECTION_EXPLICIT = 'explicit';
export const ADMIN_PROSPECT_SELECTION_ALL_MATCHING = 'all_matching';

export function createEmptyAdminProspectSelection() {
  return {
    mode: ADMIN_PROSPECT_SELECTION_EXPLICIT,
    selectedProspectIds: new Set(),
  };
}

/**
 * Enter all_matching without fetching IDs. Clones targeting so later form edits
 * cannot mutate the selection snapshot.
 * @param {Record<string, unknown> | null | undefined} targeting
 */
export function createAllMatchingAdminProspectSelection(targeting) {
  return {
    mode: ADMIN_PROSPECT_SELECTION_ALL_MATCHING,
    targeting: cloneAdminProspectTargeting(targeting),
    excludedProspectIds: new Set(),
  };
}

export function isAdminProspectSelected(selection, prospectId) {
  const id = String(prospectId ?? '');
  if (!id) return false;
  if (selection?.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING) {
    return !selection.excludedProspectIds.has(id);
  }
  return Boolean(selection?.selectedProspectIds?.has(id));
}

export function toggleAdminProspectSelection(selection, prospectId) {
  const id = String(prospectId ?? '');
  if (!id) return selection || createEmptyAdminProspectSelection();
  if (selection?.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING) {
    const excluded = new Set(selection.excludedProspectIds);
    if (excluded.has(id)) excluded.delete(id);
    else excluded.add(id);
    return { ...selection, excludedProspectIds: excluded };
  }
  const selected = new Set(selection?.selectedProspectIds || []);
  if (selected.has(id)) selected.delete(id);
  else selected.add(id);
  return { mode: ADMIN_PROSPECT_SELECTION_EXPLICIT, selectedProspectIds: selected };
}

export function isAdminProspectPageFullySelected(selection, prospectIds) {
  const ids = Array.isArray(prospectIds) ? prospectIds.filter(Boolean).map(String) : [];
  if (ids.length === 0) return false;
  return ids.every((id) => isAdminProspectSelected(selection, id));
}

export function setAdminProspectPageSelected(selection, prospectIds, selected) {
  const ids = Array.isArray(prospectIds) ? prospectIds.filter(Boolean).map(String) : [];
  if (selection?.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING) {
    const excluded = new Set(selection.excludedProspectIds);
    for (const id of ids) {
      if (selected) excluded.delete(id);
      else excluded.add(id);
    }
    return { ...selection, excludedProspectIds: excluded };
  }
  const next = new Set(selection?.selectedProspectIds || []);
  for (const id of ids) {
    if (selected) next.add(id);
    else next.delete(id);
  }
  return { mode: ADMIN_PROSPECT_SELECTION_EXPLICIT, selectedProspectIds: next };
}

export function countAdminProspectSelection(selection, totalCount = 0) {
  if (selection?.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING) {
    const total = Number.isInteger(totalCount) ? totalCount : 0;
    return Math.max(0, total - selection.excludedProspectIds.size);
  }
  return selection?.selectedProspectIds?.size || 0;
}

/**
 * Future campaign descriptor. Not sent anywhere in Lot 6.7B.
 * @param {ReturnType<typeof createEmptyAdminProspectSelection> | ReturnType<typeof createAllMatchingAdminProspectSelection>} selection
 */
export function buildAdminProspectSelectionDescriptor(selection) {
  if (selection?.mode === ADMIN_PROSPECT_SELECTION_ALL_MATCHING) {
    return {
      mode: ADMIN_PROSPECT_SELECTION_ALL_MATCHING,
      targeting: cloneAdminProspectTargeting(selection.targeting),
      excludedProspectIds: [...(selection.excludedProspectIds || [])],
    };
  }
  return {
    mode: ADMIN_PROSPECT_SELECTION_EXPLICIT,
    prospectIds: [...(selection?.selectedProspectIds || [])],
  };
}
